import { Cloud, Settings } from 'lucide-react'
import { useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'

const configurationLinks = [
  { label: 'Kong Konnect', to: '/config/kong', icon: Cloud },
  { label: 'Apigee X', to: '/config/apigee', icon: Cloud },
]

export function ConfigMenu() {
  const id = useId()
  const { pathname } = useLocation()
  const trigger = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  useLayoutEffect(() => {
    if (!open) return
    function reposition() {
      const anchor = trigger.current?.getBoundingClientRect()
      if (!anchor) return
      const width = Math.min(224, document.documentElement.clientWidth - 24)
      setPosition({
        top: anchor.bottom + 6,
        left: Math.max(
          12,
          Math.min(
            anchor.right - width,
            document.documentElement.clientWidth - width - 12,
          ),
        ),
      })
    }
    function dismissOnEscape(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      event.preventDefault()
      document.getElementById(id)?.hidePopover()
      trigger.current?.focus({ preventScroll: true })
    }
    reposition()
    window.addEventListener('keydown', dismissOnEscape)
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('keydown', dismissOnEscape)
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, id])

  return (
    <>
      <button
        ref={trigger}
        type="button"
        popoverTarget={id}
        aria-label="Configurations"
        title="Configurations"
        aria-expanded={open}
        aria-controls={id}
        aria-haspopup="dialog"
        className="grid size-8 shrink-0 place-items-center rounded-lg border border-border-default bg-raised/50 text-primary transition hover:border-accent-border/50 hover:bg-raised"
      >
        <Settings size={16} aria-hidden="true" />
      </button>
      {createPortal(
        <div
          id={id}
          popover="auto"
          role="dialog"
          aria-label="Configurations"
          onToggle={(event) => setOpen(event.newState === 'open')}
          className="fixed m-0 w-56 overflow-hidden rounded-lg border border-border-strong bg-raised p-0 text-primary shadow-lg"
          style={position}
        >
          <div className="border-b border-border-default px-4 py-3 text-xs font-semibold tracking-[0.18em] text-muted uppercase">
            Configurations
          </div>
          <nav className="p-1" aria-label="Configuration profiles">
            {configurationLinks.map(({ label, to, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                aria-current={
                  pathname === to || pathname.startsWith(`${to}/`)
                    ? 'page'
                    : undefined
                }
                onClick={() => document.getElementById(id)?.hidePopover()}
                className="flex min-h-10 items-center gap-3 rounded-md px-3 py-2 text-sm text-secondary no-underline transition-colors hover:bg-inset hover:text-primary aria-[current=page]:bg-accent-faint aria-[current=page]:text-accent"
              >
                <Icon size={16} aria-hidden="true" />
                {label}
              </Link>
            ))}
          </nav>
        </div>,
        document.body,
      )}
    </>
  )
}
